import {
  LinearProgress,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  flexRender
} from '@tanstack/react-table';
import { FC, useEffect, useMemo, useState } from 'react';
import { TableHeadCell } from '../../../../../../lib/components/compound/tables/presentation/table/head/cell';
import { TableCell } from '../../../../../../lib/components';
import {
  CompanyExtended,
  PayrollExportMapping,
  PayrollSystemEnum
} from '@pxtra-client-lib-legacy/gen';
import { ExternalIDCell } from './cells';
import { useTranslation } from 'react-i18next';
import { useCompanySettingsPayroll } from '../../../../../../queries/company';
import { useRetrievePersonioCompensationTypes } from '../../../../../../queries/personio';
export type PayrollExportMappingRow = {
  lohnart: keyof PayrollExportMapping;
  external_id: string;
};

type PayrollExportMappingTableProps = {
  company: CompanyExtended['id'];
  system: PayrollSystemEnum | undefined;
  isCellEditable?: boolean;
  tableData?: PayrollExportMapping;
  onChange?: (data: PayrollExportMapping) => void;
};

export const PayrollExportMappingTable: FC<PayrollExportMappingTableProps> = ({
  company,
  system,
  isCellEditable = false,
  onChange
}) => {
  const { t } = useTranslation(['common']);

  const { data, isRefetching, isFetching } = useCompanySettingsPayroll(company);

  const { data: typesRes } = useRetrievePersonioCompensationTypes(company, {
    enabled: system === 'PERSONIO'
  });

  const [updatedData, setUpdatedData] = useState<PayrollExportMapping | undefined>({});

  useEffect(() => {
    onChange?.(updatedData ?? {});
  }, [updatedData]);

  const getTranslatedOptions = useMemo(() => {
    if (!typesRes?.compensation_types) return [];
    return typesRes.compensation_types.map((type) => ({
      text: `${t('common:CATEGORY', { context: type.category })} - ${type.name}`,
      value: type.id
    }));
  }, [typesRes?.compensation_types, t]);

  const columnHelper = createColumnHelper<PayrollExportMappingRow>();
  const columns = useMemo(() => {
    const base = [
      columnHelper.accessor('lohnart', {
        id: 'lohnart',
        header: () => t('common:SALARY_TYPE'),
        size: 150
      }),
      columnHelper.accessor('external_id', {
        id: 'external_id',
        size: 300,
        header: () => 'External ID',
        cell: (props) => (
          <ExternalIDCell
            {...props}
            isCellEditable={isCellEditable}
            company={company}
            options={getTranslatedOptions}
            system={system}
            onUpdateRow={({ lohnart, val }) => {
              setUpdatedData((prev) => ({
                ...prev,
                [lohnart]: val
              }));
            }}
          />
        )
      })
    ];
    return base;
  }, [isCellEditable, isFetching, isRefetching, getTranslatedOptions, system]);

  const payrollExportMapping = useMemo(
    () =>
      data && !isRefetching
        ? Object.entries(data).map(
            ([key, value]) => ({ lohnart: key, external_id: value }) as PayrollExportMappingRow
          )
        : [],
    [data, isRefetching]
  );

  const table = useReactTable({
    columns,
    data: payrollExportMapping,
    getRowId: (row) => row.lohnart,
    getCoreRowModel: getCoreRowModel()
  });

  return (
    <TableContainer>
      <Table>
        <TableHead>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHeadCell
                  key={header.id}
                  colSpan={header.colSpan}
                  sx={{ width: `${header.column.getSize()}px` }}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHeadCell>
              ))}
            </TableRow>
          ))}
          {isFetching || isRefetching ? (
            <TableRow>
              <TableCell sx={{ padding: 0, height: 'unset' }} colSpan={columns.length}>
                <LinearProgress />
              </TableCell>
            </TableRow>
          ) : null}
        </TableHead>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
